import './membership-account-info.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { Provider, useDispatch } from 'react-redux';

import { LegacyAuthModalState } from '../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../auth/services/authentication-flow.service';
import { selectAccountSettings } from '../../../auth/store/auth.selectors';
import { loadAccountSettings } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { AppleWalletBtn } from '../../../common/components/wallet/apple-wallet-btn.component';
import { GoogleWalletBtn } from '../../../common/components/wallet/google-wallet-btn.component';
import { useShopifyCurrency } from '../../../common/currency/shopify-currency.hook';
import { useTranslation } from '../../../i18n';
import { modalService } from '../../../modal/modal.service';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { shopifyClient } from '../../../shopify/shopify.client';
import type { AppDispatch } from '../../../store';
import { store } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import { isAndroidMobile, isAppleMobile } from '../../../utils/platform.utils';
import type { MembershipConfirmCancelPendingActionModalProps } from '../../modals/membership-confirm-cancel-pending-action.modal';
import { MembershipConfirmCancelPendingActionModal } from '../../modals/membership-confirm-cancel-pending-action.modal';
import type { MembershipConfirmCancellationModalProps } from '../../modals/membership-confirm-cancellation.modal';
import { MembershipConfirmCancellationModal } from '../../modals/membership-confirm-cancellation.modal';
import type { MembershipManageModalProps } from '../../modals/membership-manage.modal';
import { MembershipManageModal } from '../../modals/membership-manage.modal';
import type {
  Contract,
  Membership,
  MembershipContractUpdateRequest,
} from '../../models/membership.model';
import {
  ChangeType,
  ContractState,
  PendingChangeState,
  ProductShipmentPerkType,
  WalletType,
} from '../../models/membership.model';
import {
  selectMembershipCancelling,
  selectMemberships,
  selectMembershipsLoading,
  selectMembershipUpdating,
} from '../../store/membership.selectors';
import {
  cancelMembershipUpdate,
  downloadWalletPass,
  loadAddressData,
  loadMemberships,
  updateMembership,
} from '../../store/membership.thunk';
import {
  formatDate,
  formatTermBilling,
  getMembershipTermName,
  humanize,
} from '../../utils/membership.formatters';
import {
  getMembershipLabel,
  isContractFreeTier,
  isMembershipOneTimePurchase,
  tierHasPerks,
} from '../../utils/membership.utlis';
import type { MembershipDetailRow } from '../membership-detail-table/membership-detail-table.component';
import { MembershipDetailTable } from '../membership-detail-table/membership-detail-table.component';
import { MembershipList } from '../membership-list/membership-list.component';
import { MembershipShippingBilling } from '../membership-shipping-billing/membership-shipping-billing.component';

export interface MembershipAccountInfoProps {
  membershipId?: string;
  currentProductTitle?: string;
  currentProductTitleSize?: string;
  orderHistoryUrl?: string;
  showLogin?: boolean;
  maxWidth?: boolean;
  cancelText?: string;
  showManageButton?: boolean;
  showWalletPassButton?: boolean;
  // CSS Variables
  cardBackgroundColor?: string;
  cardTextColor?: string;
  cardButtonColor?: string;
  cardButtonTextColor?: string;
  columnBorderColor?: string;
  columnBorderWidth?: string;
  borderRadius?: string;
  horizontalPadding?: string;
  verticalPadding?: string;
}

const MembershipAccountInfoComponent: FunctionComponent<MembershipAccountInfoProps> = ({
  membershipId,
  currentProductTitle = '',
  currentProductTitleSize = '',
  orderHistoryUrl = '/account',
  showLogin = false,
  maxWidth = false,
  cancelText = '',
  showManageButton = false,
  showWalletPassButton = true,
  cardBackgroundColor,
  cardTextColor,
  cardButtonColor,
  cardButtonTextColor,
  columnBorderColor,
  columnBorderWidth,
  borderRadius,
  horizontalPadding,
  verticalPadding,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { shopifyCurrency } = useShopifyCurrency();

  // Redux selectors
  const memberships = useAppSelector(selectMemberships);
  const loading = useAppSelector(selectMembershipsLoading);
  const updating = useAppSelector(selectMembershipUpdating);
  const cancelling = useAppSelector(selectMembershipCancelling);
  const accountSettingsResponse: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResponse || {};

  // CSS Variables
  const cssVars = useMemo(() => {
    const vars: Record<string, string> = {
      '--cardBackgroundColor': cardBackgroundColor ?? '',
      '--cardTextColor': cardTextColor ?? '',
      '--cardButtonColor': cardButtonColor ?? '',
      '--cardButtonTextColor': cardButtonTextColor ?? '',
      '--columnBorderColor': columnBorderColor ?? '',
      '--columnBorderWidth': columnBorderWidth ?? '',
      '--borderRadius': borderRadius ?? '',
      '--horizontalPadding': horizontalPadding ?? '',
      '--verticalPadding': verticalPadding ?? '',
    };

    if (accountSettings?.cardBackgroundColor) {
      vars['--single-settings-bg-color'] = accountSettings.cardBackgroundColor;
    }
    if (accountSettings?.cardBorderColor) {
      vars['--single-settings-brdr-color'] = accountSettings.cardBorderColor;
    }
    if (accountSettings?.cardTextColor) {
      vars['--single-settings-text-color'] = accountSettings.cardTextColor;
    }
    if (accountSettings?.cardButtonBackgroundColor) {
      vars['--single-settings-button-bg-color'] = accountSettings.cardButtonBackgroundColor;
    }
    if (accountSettings?.cardButtonTextColor) {
      vars['--single-settings-button-text-color'] = accountSettings.cardButtonTextColor;
    }
    if (accountSettings?.borderWidth) {
      vars['--single-settings-border-width'] = `${accountSettings.borderWidth}px`;
    }
    if (accountSettings?.borderRadius) {
      vars['--single-settings-border-radius'] = `${accountSettings.borderRadius}px`;
    }

    return vars;
  }, [
    cardBackgroundColor,
    cardTextColor,
    cardButtonColor,
    cardButtonTextColor,
    columnBorderColor,
    columnBorderWidth,
    borderRadius,
    horizontalPadding,
    verticalPadding,
    accountSettings?.cardBackgroundColor,
    accountSettings?.cardBorderColor,
    accountSettings?.cardTextColor,
    accountSettings?.cardButtonBackgroundColor,
    accountSettings?.cardButtonTextColor,
    accountSettings?.borderWidth,
    accountSettings?.borderRadius,
  ]);

  const COLOR_SETTINGS = [
    {
      appBlockProp: 'cardBackgroundColor',
      accountProp: 'cardBackgroundColor',
      appBlockClass: 'app-block-bg-color',
      accountClass: 'acct-settings-bg-color',
    },
    {
      appBlockProp: 'cardTextColor',
      accountProp: 'cardTextColor',
      appBlockClass: 'app-block-text-color',
      accountClass: 'acct-settings-text-color',
    },
    {
      appBlockProp: 'cardButtonColor',
      accountProp: 'cardButtonBackgroundColor',
      appBlockClass: 'app-block-button-bg-color',
      accountClass: 'acct-settings-button-bg-color',
    },
    {
      appBlockProp: 'cardButtonTextColor',
      accountProp: 'cardButtonTextColor',
      appBlockClass: 'app-block-button-text-color',
      accountClass: 'acct-settings-button-text-color',
    },
    {
      appBlockProp: 'columnBorderColor',
      accountProp: 'cardBorderColor',
      appBlockClass: 'app-block-border-color',
      accountClass: 'acct-settings-border-color',
    },
    {
      appBlockProp: 'columnBorderWidth',
      accountProp: 'borderWidth',
      appBlockClass: 'app-block-border-width',
      accountClass: 'acct-settings-border-width',
    },
    {
      appBlockProp: 'borderRadius',
      accountProp: 'borderRadius',
      appBlockClass: 'app-block-border-radius',
      accountClass: 'acct-settings-border-radius',
    },
  ];

  const useCustomClasses = (appBlockProps: any, accountSettings: any, settingsConfig: any) => {
    return useMemo(() => {
      return settingsConfig
        .map(({ appBlockProp, accountProp, appBlockClass, accountClass }: any) => {
          if (appBlockProps[appBlockProp]) return appBlockClass;
          if (accountSettings?.[accountProp]) return accountClass;
          return null;
        })
        .filter(Boolean)
        .join(' ');
    }, [appBlockProps, accountSettings, settingsConfig]);
  };

  const membershipClasses = useCustomClasses(
    {
      cardBackgroundColor,
      cardTextColor,
      cardButtonColor,
      cardButtonTextColor,
      columnBorderColor,
      columnBorderWidth,
      borderRadius,
    },
    accountSettings,
    COLOR_SETTINGS
  );

  // Local state
  const [showManage, setShowManage] = useState(false);
  const [membershipsWithPerks, setMembershipsWithPerks] = useState<Membership[]>([]);

  // Load data on mount
  useEffect(() => {
    dispatch(loadMemberships());
    dispatch(loadAddressData());
    dispatch(loadAccountSettings());
  }, [dispatch]);

  // Parse showManage query param from URL hash
  useEffect(() => {
    const url = window.location.href;
    const hashIndex = url.indexOf('#');
    if (hashIndex !== -1) {
      const hashPart = url.slice(hashIndex + 1);
      const queryStart = hashPart.indexOf('?');
      if (queryStart !== -1) {
        const hashParams = new URLSearchParams(hashPart.slice(queryStart + 1));
        const showManageParam = hashParams.get('showManage');
        if (showManageParam) {
          setShowManage(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
  }, []);

  // Filter memberships
  const filteredMemberships = useMemo(() => {
    if (!memberships) return [];
    return memberships
      .filter(
        (membership) => !membershipId || membershipId === '' || membershipId === membership.id
      )
      .filter((membership) => !!membership.contractId);
  }, [memberships, membershipId]);

  // Load product perks from Shopify (replaces forkJoin pattern)
  useEffect(() => {
    if (!filteredMemberships || filteredMemberships.length === 0) {
      setMembershipsWithPerks([]);
      return;
    }
    const loadProductPerks = async () => {
      const promises = filteredMemberships.map(async (membership) => {
        const perk = membership.selectedTier?.perks?.find(
          (p) => p.type !== ProductShipmentPerkType.AT_PURCHASE
        );
        if (!perk) {
          return membership;
        }
        try {
          const productPerk = await shopifyClient.getProduct(perk.handle, true);
          return {
            ...membership,
            currentPerk: productPerk,
          };
        } catch (err) {
          console.error('[MembershipAccountInfo]', err);
          return membership;
        }
      });
      const results = await Promise.all(promises);
      setMembershipsWithPerks(results);
    };
    loadProductPerks();
  }, [filteredMemberships]);

  // Event handlers (all memoized with useCallback)
  const handleManage = useCallback((membership: Membership) => {
    const tier = membership.tiers.find((t) => t.id === membership.selectedTier.id);
    const manageModalProps: MembershipManageModalProps = {
      membership: membership,
      tier: tier ?? membership.selectedTier,
      showCancelBtn: false,
    };
    modalService.open({
      component: MembershipManageModal,
      props: manageModalProps,
    });
  }, []);

  const handleCancelMembership = useCallback(
    (membership: Membership) => {
      const membershipLabel =
        membership?.accountSettings?.membershipLabel === 'SUBSCRIPTION'
          ? 'Subscription'
          : 'Membership';
      const confirmCancellationModalProps: MembershipConfirmCancellationModalProps = {
        membership: membership,
        title: t('membership.cancelTitle', { label: membershipLabel }),
        message: cancelText || undefined,
        onClose: (confirmed: boolean) => {
          if (confirmed) {
            const request: MembershipContractUpdateRequest = {
              contractId: membership.contractId,
              fanSubscriptionId: membership.id,
              tierId: membership.selectedTier.id,
              termId: membership.selectedTerm.id,
              changeType: ChangeType.CANCEL,
              immediate: membership.selectedTier?.freeTier || false,
            };
            dispatch(updateMembership(request));
          }
          modalService.closeAll();
        },
      };
      modalService.open({
        component: MembershipConfirmCancellationModal,
        props: confirmCancellationModalProps,
      });
    },
    [cancelText, dispatch]
  );

  const handleCancelPendingAction = useCallback(
    (membership: Membership) => {
      const membershipLabel = getMembershipLabel(membership);
      let changeType = t('membership.termChange');
      switch (membership.latestPendingChange) {
        case PendingChangeState.CANCEL:
          changeType = t('membership.labelCancellation', { label: membershipLabel });
          break;
        case PendingChangeState.DOWNGRADE:
          changeType = t('membership.downgrade');
          break;
        case PendingChangeState.UPGRADE:
          changeType = t('membership.upgrade');
          break;
      }
      const confirmCancelPendingActionModalProps: MembershipConfirmCancelPendingActionModalProps = {
        title: t('membership.cancelPendingTitle', { change: changeType }),
        message: t('membership.revertSettings', { label: membershipLabel.toLowerCase() }),
        confirmText: t('membership.yesCancelPending', { change: changeType }),
        onConfirm: () => {
          dispatch(
            cancelMembershipUpdate({
              contractId: membership.contractId,
              fanSubscriptionId: membership.id,
            })
          );
          modalService.closeAll();
        },
        onCancel: () => {
          modalService.closeAll();
        },
      };
      modalService.open({
        component: MembershipConfirmCancelPendingActionModal,
        props: confirmCancelPendingActionModalProps,
      });
    },
    [dispatch]
  );

  const handleViewProduct = useCallback((e: Event, handle: string) => {
    e.stopImmediatePropagation();
    const url = `${window.location.origin}/products/${handle}`;
    window.open(url, '_blank', 'noopener');
  }, []);

  const handleOpenLoginModal = useCallback(() => {
    authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [LegacyAuthModalState.LOGIN, LegacyAuthModalState.RESET_PASSWORD],
    });
  }, []);

  const handleDownloadAppleWalletPass = useCallback(
    (contract: Contract) => {
      dispatch(downloadWalletPass({ contract, walletType: WalletType.APPLE }));
    },
    [dispatch]
  );

  const handleDownloadGoogleWalletPass = useCallback(
    (contract: Contract) => {
      dispatch(downloadWalletPass({ contract, walletType: WalletType.GOOGLE }));
    },
    [dispatch]
  );

  // SNGL-7386: the Membership table -- plan, IDs and, while one is scheduled, the pending change
  const membershipRows = (membership: Membership): MembershipDetailRow[] => {
    const rows: MembershipDetailRow[] = [];
    if (membership.selectedTier.title) {
      rows.push({
        key: 'plan',
        label: t('membership.detailPlan'),
        value: <p>{membership.selectedTier.title}</p>,
        actions: showManageButton &&
          membership.tiers.length > 1 &&
          !isMembershipOneTimePurchase(membership) && (
            <span onClick={() => setShowManage(true)}>{t('membership.upgradeDowngrade')}</span>
          ),
      });
    }
    rows.push({
      key: 'member-id',
      label: t('membership.detailMemberId'),
      value: <p className="membership-id">{membership.contract?.id}</p>,
      actions: isContractFreeTier(membership.contract) && !membership.pendingChanges && (
        <span onClick={() => handleCancelMembership(membership)}>
          {t('membership.cancelMembershipButton')}
        </span>
      ),
    });
    if (
      !isContractFreeTier(membership.contract) &&
      !isMembershipOneTimePurchase(membership) &&
      membership.contract?.sourceContractId
    ) {
      rows.push({
        key: 'subscription-number',
        label: t('membership.detailSubscriptionNumber'),
        value: <p className="membership-id">{membership.contract.sourceContractId}</p>,
      });
    }
    if (
      !isContractFreeTier(membership.contract) &&
      !isMembershipOneTimePurchase(membership) &&
      membership.pendingChanges
    ) {
      rows.push({
        key: 'status',
        label: t('membership.detailStatus'),
        value: (
          <p>
            {membership.latestPendingChange === PendingChangeState.CANCEL
              ? t('membership.cancellation')
              : humanize(membership.latestPendingChange)}{' '}
            {t('membership.hasBeenScheduled')}
            {membership.latestPendingChange === PendingChangeState.TERM_CHANGE &&
              membership.contract.latestPendingChange.term && (
                <span>
                  {' '}
                  {t('membership.changingTo')}{' '}
                  <b>{getMembershipTermName(membership.contract.latestPendingChange.term)}</b> (
                  {formatTermBilling(membership.contract.latestPendingChange.term)})
                </span>
              )}
            {membership.latestPendingChange === PendingChangeState.DOWNGRADE && (
              <>
                {' '}
                {t('membership.willBeDowngradedTo', {
                  label: getMembershipLabel(membership).toLowerCase(),
                })}{' '}
                {membership.contract.latestPendingChange.tier.title} {t('membership.on')}{' '}
                <b>{formatDate(membership.nextBillingDate)}</b>.
              </>
            )}
            {membership.latestPendingChange === PendingChangeState.CANCEL && (
              <>
                {' '}
                {t('membership.stillHaveAccessUntil')}{' '}
                <b>{formatDate(membership.nextBillingDate)}</b>.
              </>
            )}
          </p>
        ),
        actions: (
          <a onClick={() => handleCancelPendingAction(membership)}>
            {t('membership.cancelChange')}
          </a>
        ),
      });
    }
    return rows;
  };

  // SNGL-7386: the Billing table. Terms and Next Billing are recurring-only; Last Payment shows for
  // one-time purchases too, as the payment line did.
  const billingRows = (membership: Membership): MembershipDetailRow[] => {
    const rows: MembershipDetailRow[] = [];
    if (!isMembershipOneTimePurchase(membership)) {
      rows.push({
        key: 'terms',
        label: t('membership.detailTerms'),
        value: (
          <p>
            {getMembershipTermName(membership.selectedTerm)} (
            {formatTermBilling(membership.selectedTerm)})
          </p>
        ),
        actions: !membership.pendingChanges && (
          <>
            <span onClick={() => handleManage(membership)}>{t('common.edit')}</span>
            <span onClick={() => handleCancelMembership(membership)}>
              {t('membership.cancelLink')}
            </span>
          </>
        ),
      });
      rows.push({
        key: 'next-billing',
        label: t('membership.nextBilling'),
        value:
          membership.latestPendingChange !== PendingChangeState.CANCEL ? (
            <>
              <p>{formatDate(membership.nextBillingDate)}</p>
              {tierHasPerks(membership.selectedTier) && (
                <p>{t('membership.subscriptionShipMonthly')}</p>
              )}
            </>
          ) : (
            <p>{t('membership.noUpcomingBillDates')}</p>
          ),
        actions: (
          <a href={orderHistoryUrl || '/account'} target="_blank" rel="noopener">
            {t('membership.orderHistory')}
          </a>
        ),
      });
    }
    const lastPayment = membership.contract.latestBillingAttempt;
    if (lastPayment) {
      rows.push({
        key: 'last-payment',
        label: t('membership.detailLastPayment'),
        value: (
          <>
            <p>
              {lastPayment.orderAmount > 0
                ? t('membership.receivedAmountOn', {
                    amount: shopifyCurrency(lastPayment.orderAmount, false),
                    date: formatDate(lastPayment.createdAt),
                  })
                : t('membership.receivedOn', { date: formatDate(lastPayment.createdAt) })}
            </p>
            {tierHasPerks(membership.selectedTier) && <p>{t('membership.emailWithTracking')}</p>}
          </>
        ),
      });
    }
    return rows;
  };

  return (
    <div className={`membership-account-info ${membershipClasses}`} style={cssVars}>
      {/* Login Prompt */}
      {showLogin && (
        <div className="membership-account-info-login page-width">
          <button class="btn button" onClick={handleOpenLoginModal}>
            {t('auth.logIn')}
          </button>
        </div>
      )}
      {/* Loading/Updating/Cancelling Spinner */}
      {!showLogin && (loading || updating || cancelling) && (
        <section
          class="single-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '16 / 9',
            maxHeight: '75vh',
            width: '100%',
          }}
        >
          <SpinnerComponent />
        </section>
      )}
      {/* Account Info */}
      {!showLogin && !loading && !updating && !cancelling && (
        <>
          <div className={`acct-info-wrapper ${maxWidth ? 'page-width' : ''}`}>
            {membershipsWithPerks.map((membership) => (
              <div key={membership.id} className="acct-info">
                {!showManage ? (
                  <>
                    {/* Account Info Inner - Complex grid layout */}
                    <div
                      className={`acct-info-inner ${
                        !membership.currentPerk?.images[0] && !membership.selectedTier.imageUrl
                          ? 'one-col'
                          : ''
                      } ${isContractFreeTier(membership.contract) ? 'free-tier' : ''}`}
                    >
                      {/* Image Column */}
                      {(membership.currentPerk?.images[0] || membership.selectedTier.imageUrl) && (
                        <div className="acct-info-col image">
                          <img
                            src={
                              membership.currentPerk?.images[0] ?? membership.selectedTier.imageUrl
                            }
                            alt={t('membership.productImageAlt')}
                          />
                        </div>
                      )}
                      {/* Info Column */}
                      <div className="acct-info-col">
                        {/* Header row: club name left, status badge right */}
                        <div className="acct-info-header">
                          <h3 className="h3 single-h3">{membership.title}</h3>
                          <div
                            className={`status-badge ${`badge-${membership.contract?.state?.toLowerCase()}`} ${membership.pendingChanges ? 'badge-pending' : ''}`}
                          >
                            {membership.pendingChanges ? (
                              <>
                                {t('membership.pending')}{' '}
                                {membership.latestPendingChange === PendingChangeState.CANCEL
                                  ? t('membership.cancellation')
                                  : ((
                                      {
                                        [PendingChangeState.UPGRADE]: t('membership.upgrade'),
                                        [PendingChangeState.DOWNGRADE]: t('membership.downgrade'),
                                        [PendingChangeState.TERM_CHANGE]:
                                          t('membership.termChange'),
                                      } as Record<string, string>
                                    )[membership.latestPendingChange] ??
                                    humanize(membership.latestPendingChange))}
                              </>
                            ) : (
                              ((
                                {
                                  [ContractState.ACTIVE]: t('membership.active'),
                                  [ContractState.PENDING]: t('membership.pending'),
                                  [ContractState.UPGRADE]: t('membership.upgrade'),
                                  [ContractState.DOWNGRADE]: t('membership.downgrade'),
                                  [ContractState.PAYMENT_FAILED]: t('membership.paymentFailed'),
                                } as Record<string, string>
                              )[membership.contract?.state] ?? humanize(membership.contract?.state))
                            )}
                          </div>
                        </div>
                        <MembershipDetailTable
                          caption={t('membership.detailMembership')}
                          rows={membershipRows(membership)}
                        />
                        {/* Product Perk Info with Next Shipment */}
                        {tierHasPerks(membership.selectedTier) && membership.currentPerk && (
                          <div className="product-info">
                            <div className="product-info-header">
                              <div className="status-badge">
                                {t('membership.nextShipment')}{' '}
                                {formatDate(membership.contract.nextShippingDate, 'MMM d, y')}
                              </div>
                              <span
                                className="single-product-perk-view-product"
                                onClick={(e) => handleViewProduct(e, membership.currentPerk.handle)}
                              >
                                {t('membership.viewProduct')}
                              </span>
                            </div>
                            <h5
                              className={`current-product-title single-${currentProductTitleSize} ${currentProductTitleSize}`}
                            >
                              {currentProductTitle}
                            </h5>
                            <h4 className="h4 single-h4">{membership.currentPerk.title}</h4>
                            <p
                              dangerouslySetInnerHTML={{
                                __html: membership.currentPerk.description,
                              }}
                            />
                          </div>
                        )}
                        {/* Billing (terms, next billing, last payment) -- not for free tiers */}
                        {!isContractFreeTier(membership.contract) && (
                          <MembershipDetailTable
                            caption={t('membership.detailBilling')}
                            rows={billingRows(membership)}
                          />
                        )}
                        {/* Wallet Pass Download Buttons — hidden where a page
                            provides the permanent wallet section (SNGL-7029) */}
                        {showWalletPassButton &&
                          membership.contract.tier?.walletTheme?.enabled &&
                          (isAppleMobile() || isAndroidMobile()) && (
                            <div class="single-wallet-links">
                              {isAppleMobile() && (
                                <AppleWalletBtn
                                  onClick={() => handleDownloadAppleWalletPass(membership.contract)}
                                />
                              )}
                              {isAndroidMobile() && (
                                <GoogleWalletBtn
                                  onClick={() =>
                                    handleDownloadGoogleWalletPass(membership.contract)
                                  }
                                />
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                    {/* Membership Shipping/Billing Component */}
                    {membership.contract && !isContractFreeTier(membership.contract) && (
                      <MembershipShippingBilling membership={membership} />
                    )}
                  </>
                ) : (
                  <>
                    {/* showManage Mode - Display MembershipList for Upgrade/Downgrade */}
                    <span onClick={() => setShowManage(false)} class="go-back">
                      {t('membership.back')}
                    </span>
                    <MembershipList
                      memberships={[membership]}
                      accountSettings={accountSettingsResponse.accountSettings}
                      showProgramName={false}
                      showTierImage={true}
                      showTierName={true}
                      showTierDescription={true}
                      showPrice={true}
                      showQuickAdd={false}
                      showViewPerks={false}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipAccountInfo = memo(MembershipAccountInfoComponent);

// Wrapper with Redux Provider for standalone widget usage
export const MembershipAccountInfoWithStore: FunctionComponent<MembershipAccountInfoProps> = (
  props
) => {
  return (
    <Provider store={store}>
      <MembershipAccountInfo {...props} />
    </Provider>
  );
};
